/* eslint-disable no-nested-ternary */
import Select from 'new-components/Select'
import Input from 'new-components/Input'
import { formatCpf, formatPhone, dateMask } from 'utils/formatters'
import useRegistration, { RegistrationPath } from 'pages/Registration/hooks/useRegistration'
import CheckboxInput from 'new-components/CheckboxInput'
import { Link } from 'react-router-dom'
import { MOBILE_WIDTH } from 'utils/constants'
import useMediaQuery from 'hooks/useMediaQuery'
import Loading from 'components/Loading'
import * as SharedStyles from '../../../styles'
import * as Styles from './styles'
import { GENDER_IDENTITY_LIST, NATIONALITY_LIST } from './constants'
import { GENDER_OPTIONS } from '../../constants'
import { getFieldError } from '../../helpers'
import useRegistrationForm from '../../hooks/useRegistrationForm'
import { Button } from '@cannect/new-components/atoms'

// Helper component para inputs padronizados como no login
const LoginStyleInput = ({
  id,
  label,
  value,
  onChange,
  errorMessage,
  placeholder = '',
  type,
  labelWidth = 'auto'
}: {
  id: string
  label: string
  value: string
  onChange: (event: any) => void
  errorMessage?: string
  placeholder?: string
  type?: string
  labelWidth?: string
}) => {
  const getPaddingLeft = () => {
    if (labelWidth === 'auto') {
      const labelLength = label.length
      if (labelLength <= 8) return 'pl-16 md:pl-20'
      if (labelLength <= 15) return 'pl-32 md:pl-36'
      return 'pl-40 md:pl-44'
    }
    return labelWidth
  }

  return (
    <div className="relative">
      {!value && (
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 rounded-md px-2 py-1 text-xs font-semibold text-neutral-500">
          {label}
        </span>
      )}
      <Input
        id={id}
        placeholder=""
        value={value}
        errorMessage={errorMessage}
        onChange={onChange}
        type={type}
        className={`h-12 w-full appearance-none rounded-lg bg-white pr-12 text-sm text-neutral-800 ${value ? 'pl-4' : getPaddingLeft()} [&:-webkit-autofill]:text-neutral-800 [&:-webkit-autofill]:shadow-[inset_0_0_0_1000px_#ffffff] [&:-webkit-autofill]:[transition:background-color_5000s_ease-in-out_0s]`}
      />
    </div>
  )
}

interface RegistrationFormProps {
  onGoBack: () => void
  path: RegistrationPath
}

export default function RegistrationForm({ onGoBack, path }: RegistrationFormProps) {
  const isMobile = useMediaQuery(MOBILE_WIDTH)
  const isProfessional = path === 'professional'
  const registrationFields = useRegistration()
  const {
    typeOfBoard,
    loading,
    name,
    nationality,
    preferredName,
    setPreferredName,
    genderIdentity,
    cpf,
    rg,
    rne,
    birthdate,
    telephone,
    setTelephone,
    cellphone,
    specialties,
    changeSpecialties,
    email,
    emailConfirmation,
    password,
    passwordConfirmation,
    gender,
    changeFieldValue,
    errors
  } = registrationFields

  const {
    onRegistrationFormSubmit,
    changeAgreedToUseTerms,
    setReceivePromotionalEmails,
    agreedToUseTerms,
    receivePromotionalEmails,
    specialtiesList
  } = useRegistrationForm(registrationFields)

  const genderField = (
    <Select
      id="gender"
      label="Sexo*"
      placeholder="Selecione uma opção"
      options={GENDER_OPTIONS}
      value={gender}
      errorMessage={getFieldError('gender', errors)}
      onChange={(newValue) => changeFieldValue('gender', newValue)}
    />
  )

  return (
    <Styles.RegistrationFormContainer onSubmit={onRegistrationFormSubmit}>
      <Loading loading={loading} />
      <SharedStyles.InputsContainer>
        <Styles.VariableWidthContainer width="67%">
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 rounded-md px-2 py-1 text-xs font-semibold text-neutral-500">
              Nome completo*
            </span>
            <Input
              id="name"
              placeholder=""
              value={name}
              errorMessage={getFieldError('name', errors)}
              onChange={(event) => changeFieldValue('name', event.target.value)}
              className="h-12 w-full appearance-none rounded-lg bg-white pl-40 pr-4 text-sm text-neutral-800 md:pl-44 [&:-webkit-autofill]:text-neutral-800 [&:-webkit-autofill]:shadow-[inset_0_0_0_1000px_#ffffff] [&:-webkit-autofill]:[transition:background-color_5000s_ease-in-out_0s]"
            />
          </div>
        </Styles.VariableWidthContainer>
        <Styles.VariableWidthContainer width="33%">
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 rounded-md px-2 py-1 text-xs font-semibold text-neutral-500">
              CPF*
            </span>
            <Input
              id="cpf"
              placeholder=""
              value={cpf}
              errorMessage={getFieldError('cpf', errors)}
              onChange={(event) => changeFieldValue('cpf', formatCpf(event.target.value))}
              className="h-12 w-full appearance-none rounded-lg bg-white pl-16 pr-4 text-sm text-neutral-800 md:pl-20 [&:-webkit-autofill]:text-neutral-800 [&:-webkit-autofill]:shadow-[inset_0_0_0_1000px_#ffffff] [&:-webkit-autofill]:[transition:background-color_5000s_ease-in-out_0s]"
            />
          </div>
        </Styles.VariableWidthContainer>
      </SharedStyles.InputsContainer>

      {isProfessional && (
        <SharedStyles.InputsContainer>
          <LoginStyleInput
            id="preferred-name"
            label="Nome de preferência"
            value={preferredName}
            onChange={(event) => setPreferredName(event.target.value)}
            labelWidth="pl-40 md:pl-44"
          />
          <Select
            id="gender-identity"
            label="Identidade de gênero*"
            options={GENDER_IDENTITY_LIST}
            value={genderIdentity}
            errorMessage={getFieldError('genderIdentity', errors)}
            onChange={(newValue) => changeFieldValue('genderIdentity', newValue)}
          />
          <Select
            id="nationality"
            label="Nacionalidade*"
            options={NATIONALITY_LIST}
            value={nationality}
            errorMessage={getFieldError('nationality', errors)}
            onChange={(newValue) => changeFieldValue('nationality', newValue)}
          />
        </SharedStyles.InputsContainer>
      )}

      {isProfessional && (
        <SharedStyles.InputsContainer>
          {nationality === 'estrangeira' ? (
            <LoginStyleInput
              id="rne"
              label="RNE*"
              value={rne}
              errorMessage={getFieldError('rne', errors)}
              onChange={(event) => changeFieldValue('rne', event.target.value)}
              labelWidth="pl-16 md:pl-20"
            />
          ) : (
            <Styles.VariableWidthContainer width="50%">
              <LoginStyleInput
                id="rg"
                label="RG*"
                value={rg}
                errorMessage={getFieldError('rg', errors)}
                onChange={(event) => changeFieldValue('rg', event.target.value)}
                labelWidth="pl-16 md:pl-20"
              />
            </Styles.VariableWidthContainer>
          )}
          <Styles.VariableWidthContainer width="50%">{genderField}</Styles.VariableWidthContainer>
          {typeOfBoard === 'CRM' && (
            <LoginStyleInput
              id="telephone"
              label="Telefone"
              value={telephone}
              onChange={(event) => setTelephone(formatPhone(event.target.value))}
              labelWidth="pl-24 md:pl-28"
            />
          )}
        </SharedStyles.InputsContainer>
      )}

      <SharedStyles.InputsContainer>
        <LoginStyleInput
          id="birthdate"
          label="Data de nascimento*"
          value={birthdate}
          errorMessage={getFieldError('birthdate', errors)}
          onChange={(event) => changeFieldValue('birthdate', dateMask(event.target.value))}
          labelWidth="pl-36 md:pl-40"
        />
        <LoginStyleInput
          id="cellphone"
          label="Celular/WhatsApp*"
          value={cellphone}
          errorMessage={getFieldError('cellphone', errors)}
          onChange={(event) => changeFieldValue('cellphone', formatPhone(event.target.value))}
          labelWidth="pl-36 md:pl-40"
        />
      </SharedStyles.InputsContainer>

      {!isProfessional && <SharedStyles.InputsContainer>{genderField}</SharedStyles.InputsContainer>}

      <SharedStyles.InputsContainer>
        <Styles.VariableWidthContainer width="67%">
          <LoginStyleInput
            id="email"
            label="E-mail*"
            type="email"
            value={email}
            errorMessage={getFieldError('email', errors)}
            onChange={(event) => changeFieldValue('email', event.target.value)}
            labelWidth="pl-24 md:pl-28"
          />
        </Styles.VariableWidthContainer>
        <Styles.VariableWidthContainer width="33%">
          <LoginStyleInput
            id="email-confirmation"
            label="Confirmação do e-mail*"
            type="email"
            value={emailConfirmation}
            errorMessage={getFieldError('emailConfirmation', errors)}
            onChange={(event) => changeFieldValue('emailConfirmation', event.target.value)}
            labelWidth="pl-44 md:pl-48"
          />
        </Styles.VariableWidthContainer>
      </SharedStyles.InputsContainer>

      <SharedStyles.InputsContainer>
        <Styles.VariableWidthContainer width="50%">
          <LoginStyleInput
            id="password"
            label="Senha*"
            type="password"
            value={password}
            errorMessage={getFieldError('password', errors)}
            onChange={(event) => changeFieldValue('password', event.target.value)}
            labelWidth="pl-20 md:pl-24"
          />
          <Styles.PasswordGuidelines>
            A senha deve ter 8 caracteres com pelo menos 1 letra maiúscula, 1 letra minúscula, 1 número e 1 caractere
            especial
          </Styles.PasswordGuidelines>
        </Styles.VariableWidthContainer>
        <Styles.VariableWidthContainer width="50%">
          <LoginStyleInput
            id="password-confirmation"
            label="Confirmação da senha*"
            type="password"
            value={passwordConfirmation}
            errorMessage={getFieldError('passwordConfirmation', errors)}
            onChange={(event) => changeFieldValue('passwordConfirmation', event.target.value)}
            labelWidth="pl-44 md:pl-48"
          />
          <Styles.PasswordGuidelines>
            A senha deve ter 8 caracteres com pelo menos 1 letra maiúscula, 1 letra minúscula, 1 número e 1 caractere
            especial
          </Styles.PasswordGuidelines>
        </Styles.VariableWidthContainer>
      </SharedStyles.InputsContainer>

      <CheckboxInput
        checked={agreedToUseTerms}
        label={
          <p>
            Eu concordo com os termos e{' '}
            <Link to="politica-de-privacidade" target="_blank">
              condições e a política de privacidade
            </Link>
          </p>
        }
        hasError={!!getFieldError('agreedToUseTerms', errors)}
        onChange={changeAgreedToUseTerms}
        id="agreed-to-use-terms"
      />
      <CheckboxInput
        checked={receivePromotionalEmails}
        label="Quero receber e-mails promocionais e novidades sobre a Cannect"
        onChange={() => setReceivePromotionalEmails((state) => !state)}
        id="receive-promotional-emails"
      />

      <SharedStyles.ConfirmRegistrationContainer>
        <Button variant="outline" type="button" onClick={onGoBack}>
          Voltar
        </Button>
        <Button type="submit">Fazer inscrição</Button>
      </SharedStyles.ConfirmRegistrationContainer>
    </Styles.RegistrationFormContainer>
  )
}
